import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { isAuthenticated, login, logout } from '../utils/authUtils';
import { startTokenCheck, stopTokenCheck, isTokenValid } from '../utils/tokenManager';
import { useTranslation } from 'react-i18next';
import { apiService } from '../api/api.service';
import { store } from '../redux/store';
import { resetStore } from '../redux/actions/resetStore';
import type { IAdmin } from '../types';

// Тип для контекста авторизации
interface AuthContextType {
    authenticated: boolean;
    loading: boolean;
    login: (email: string, password: string) => Promise<any>;
    logout: () => Promise<void>;
    error: string | null;
    admin: IAdmin | null;
    fetchAdminInfo: () => Promise<void>;
}

// Создаём контекст с дефолтными значениями
const AuthContext = createContext<AuthContextType>({
    authenticated: false,
    loading: true,
    login: async () => null,
    logout: async () => { },
    error: null,
    admin: null,
    fetchAdminInfo: async () => { },
});

// Провайдер авторизации
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [admin, setAdmin] = useState<IAdmin | null>(null);

    const fetchAdminInfo = useCallback(async () => {
        try {
            const adminInfo = await apiService.adminInfo();
            setAdmin(adminInfo);
        } catch (err) {
            console.error('Failed to fetch admin info', err);
            // Возможно, стоит обработать ошибку, например, разлогинить пользователя
        }
    }, []);

    // Проверка авторизации при загрузке
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const isAuth = isAuthenticated();
                setAuthenticated(isAuth);

                if (isAuth) {
                    // Запускаем менеджер токенов и получаем информацию админа
                    startTokenCheck();
                    await fetchAdminInfo();
                }
            } catch (err) {
                console.error(t('errors.authCheckError'), err);
                // При ошибке проверяем валидность токена
                if (!isAuthenticated()) {
                    setAuthenticated(false);
                }
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, [t, fetchAdminInfo]);

    // Управление менеджером токенов при изменении состояния авторизации
    useEffect(() => {
        if (authenticated) {
            // Запускаем менеджер токенов при авторизации
            startTokenCheck();
        } else {
            // Останавливаем менеджер токенов при выходе
            stopTokenCheck();
        }

        return () => {
            // Очищаем при размонтировании
            stopTokenCheck();
        };
    }, [authenticated]);

    // Обработчик событий ошибок авторизации
    useEffect(() => {
        const handleUnauthorized = async () => {
            try {
                // Проверяем валидность текущего токена
                if (!isTokenValid()) {
                    await handleLogout();
                }
            } catch (err) {
                // Проверяем валидность текущего токена перед разлогинированием
                if (!isTokenValid()) {
                    await handleLogout();
                }
            }
        };

        // Слушаем события 401 ошибок
        window.addEventListener('auth:unauthorized', handleUnauthorized);

        return () => {
            window.removeEventListener('auth:unauthorized', handleUnauthorized);
        };
    }, [t]);

    // Функция входа
    const handleLogin = async (email: string, password: string) => {
        setLoading(true);
        setError(null);

        try {
            const result = await login(email, password);
            if (!result) {
                // Если login() вернул false или null, аутентификация не удалась
                setError(t('errors.loginFailed'));
                setAuthenticated(false);
                return null;
            }

            const adminCheck = await apiService.adminCheck();
            if (!adminCheck.is_admin) {
                await handleLogout();
                setError(t('errors.notAdminError'));
                return null;
            }

            setAuthenticated(true);
            await fetchAdminInfo();
            return result;

        } catch (err: any) {
            setError(err.message || t('errors.loginFailed'));
            setAuthenticated(false);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Функция выхода
    const handleLogout = async () => {
        try {
            // Сбрасываем Redux store перед logout
            store.dispatch(resetStore());

            // Выполняем logout (очистка токенов и кеша)
            await logout();
        } finally {
            setAuthenticated(false);
            setAdmin(null);
            // Останавливаем менеджер токенов при выходе
            stopTokenCheck();
        }
    };

    // Предоставляем контекст компонентам
    return (
        <AuthContext.Provider
            value={{
                authenticated,
                loading,
                login: handleLogin,
                logout: handleLogout,
                error,
                admin,
                fetchAdminInfo
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

// Хук для использования контекста авторизации
export const useAuth = () => useContext(AuthContext); 